import { catchError } from 'rxjs/operators';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';

import { Store } from '@ngrx/store';

import { Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilKeyChanged, skip, take } from 'rxjs/operators';
import { PartsService } from '../../services/parts.service';

import { loadPage, updateCurrentPageNumber, updateIsPartsVisible } from '../../store/actions/config.actions';
import { CONFIG } from '../../store/reducer/config.reducer';
import { DATA } from '../../store/reducer/data.reducer';

import * as d3 from 'd3';
import { environment } from '../../../../environments/environment';

function dragstarted() {
  if (isScaleActive) {
    d3.select(this).raise();
    d3.select(this).attr('cursor', 'grabbing');
  }
}
let isScaleActive = false;
let p = {
  x: 0,
  y: 0
};

function dragged(d) {
  if (isScaleActive) {
    p.y += d.dy;
    p.x += d.dx;
    d3.select(this).attr('transform', 'translate(' + p.x + ',' + p.y + ')');
  }
}

function dragended() {
  if (isScaleActive) {
    d3.select(this).attr('cursor', 'grab');
  }
}

@Component({
    selector: 'app-parts-view',
    templateUrl: './parts-view.component.html',
    styleUrls: ['./parts-view.component.scss'],
    standalone: false
})
export class PartsViewAppComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pageId: string;

  config$: Observable<CONFIG> = this.store.select((state) => state.config);

  @ViewChild('content') content!: ElementRef;

  svgRendered = false;
  loading = false;
  isLoaded = false;

  private router$: Subscription;
  private config: CONFIG;
  private renderSVG$ = new Subject();

  constructor(private store: Store<{ config: CONFIG; data: DATA }>, private partsService: PartsService) {}

  ngOnInit() {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageId'] && changes['pageId'].currentValue !== changes['pageId'].previousValue) {
      this.load(this.pageId);
    }
  }

  toggleIsPartsVisible() {
    this.store.dispatch(updateIsPartsVisible());
  }

  ngOnDestroy() {
    this.router$ && this.router$.unsubscribe();
  }

  private loadData() {
    let paramsId;
    this.store.dispatch(updateCurrentPageNumber(parseInt(this.pageId)));
    paramsId = this.pageId;
    if (this.config) {
      this.load(paramsId);
    }

    this.config$.pipe(distinctUntilKeyChanged('scale'), skip(1)).subscribe((config: any) => {
      isScaleActive = config.scale !== 100;
      this.config = config;
    });

    this.config$.pipe(take(1)).subscribe((config: any) => {
      this.config = config;
      if (paramsId) {
        this.load(paramsId);
      }
    });

    this.renderSVG$.pipe(debounceTime(400)).subscribe((response) => {
      this.partsService.getPartsViewApiUrl().subscribe((apiUrl) => {
        this.renderSVG(response, apiUrl);
      });
    });
  }

  private load(id: string) {
    d3.select('#svgWrapper').html('');
    p = {
      x: 0,
      y: 0
    };
    this.svgRendered = false;
    this.loading = true;
    this.partsService
      .loadSVG(id)
      .pipe(catchError((err) => this.errorHandler(err)))
      .subscribe((response) => {
        this.partsService.loadedSVG[id] = response;
        response.isMain = id == '0';
        this.renderSVG$.next(response);
        this.store.dispatch(loadPage(id));
      });
  }

  private renderSVG(data: any, apiUrl: string) {
    let d = data['xml'].documentElement;
    const gs = (d as HTMLElement).querySelectorAll('g');
    if (!gs) {
      this.errorHandler('unable to find g node in SVG');
      return;
    }
    const svg = d3.select('#svgWrapper').append('svg');

    for (let index = 0; index < d.attributes.length; index++) {
      const element = d.attributes[index];
      svg.attr(element.nodeName, element.value);
    }

    if (data.isMain) {
      gs.forEach((g) => svg.node().appendChild(g));
    } else {
      svg.node().appendChild(gs[0]);
    }
    const dessins = document.querySelectorAll('#dessin');
    dessins.forEach((dessin) => {
      dessin?.setAttribute(
        'href',
        !dessin.getAttribute('href').includes('http')
          ? `${apiUrl}/partviews/${this.config.uid}/svg/${dessin.getAttribute('href')}`
          : dessin.getAttribute('href')
      );
    });

    const plan = svg.select('g');
    plan.call(d3.drag().on('start', dragstarted).on('drag', dragged).on('end', dragended));
    this.svgRendered = true;
    this.loading = false;
    this.isLoaded = true;
  }

  private errorHandler(error): Observable<any> {
    this.loading = false;
    this.svgRendered = false;
    this.isLoaded = true;
    console.error('error', error);
    return of(false);
  }
}
